/**
 * Country registry — typed accessor over docs/ontology/codegen.json#countries.
 *
 * Country is the umbrella that drives currency, locale, statutory rule pack,
 * climate-zone classification, cultural rule pack, default vendor connectors,
 * date format, and unit system. Per CLAUDE.md §1, this module is the only
 * place that *defines* the list — every consumer reads from here.
 *
 * Default country is `VITE_DEFAULT_COUNTRY` (env-driven, never literal).
 */
import codegen from "../../../../docs/ontology/codegen.json";
import { env } from "../env";

export interface CompactSuffix {
  /** Threshold at which this suffix applies. e.g. 100000 → "L" for INR */
  readonly n: number;
  readonly suffix: string;
}

export interface Country {
  /** ISO 3166-1 alpha-2 code, e.g. "IN" */
  readonly code: string;
  readonly label: string;
  /** ISO 4217, e.g. "INR" */
  readonly currency: string;
  /** Glyph rendered next to amounts. */
  readonly currencySymbol: string;
  /** BCP 47, used with `Intl.NumberFormat`. */
  readonly locale: string;
  /** Country-specific compact units (L/Cr for INR, M/B for USD, …).
   *  Ordered descending — first matching threshold wins. */
  readonly compactSuffixes: ReadonlyArray<CompactSuffix>;
  readonly statutoryRulePack: ReadonlyArray<string>;
  readonly climateZoneSystem: string;
  readonly culturalRulePack: string;
  readonly defaultVendors: ReadonlyArray<string>;
  readonly dateFormat: string;
  readonly unitSystem: "metric" | "imperial";
}

const COUNTRIES: ReadonlyArray<Country> = (codegen.countries ?? []) as ReadonlyArray<Country>;
const BY_CODE: ReadonlyMap<string, Country> = new Map(COUNTRIES.map((c) => [c.code, c]));

if (COUNTRIES.length === 0) {
  // CLAUDE.md §5 — surface, never silently default.
  throw new Error(
    "lib/i18n/country.ts: codegen.json#countries is empty. " +
    "Run `cd web && npm run gen:ontology` after editing docs/ontology/codegen.json.",
  );
}

export function listCountries(): ReadonlyArray<Country> {
  return COUNTRIES;
}

export function countryByCode(code: string): Country | undefined {
  return BY_CODE.get(code);
}

/** Active country for this session. Reads `VITE_DEFAULT_COUNTRY` from env;
 *  per CLAUDE.md §1 there is *no* fallback to a baked-in default — the env
 *  must declare it explicitly. */
export function activeCountry(): Country {
  const code = env.VITE_DEFAULT_COUNTRY;
  const c = BY_CODE.get(code);
  if (!c) {
    throw new Error(
      `VITE_DEFAULT_COUNTRY="${code}" is not in the registry. ` +
      `Known codes: ${COUNTRIES.map((x) => x.code).join(", ")}.`,
    );
  }
  return c;
}
